import {
  parseUrlPathname,
  scrubRynReplayPathname,
  scrubRynReplayUrl,
} from '@ups/xplat/base/ryn-replay';
import { maskRynReplayEventSsn } from './ryn-replay-rrweb-ssn-scrub';

/**
 * rrweb `EventType` numbers this scrub acts on. Kept local and explicit (the
 * same reason the recorder keeps its own type-name table) so a rrweb major
 * version cannot silently renumber which events get scrubbed.
 */
const RRWEB_FULL_SNAPSHOT_EVENT_TYPE = 2;
const RRWEB_INCREMENTAL_SNAPSHOT_EVENT_TYPE = 3;
const RRWEB_META_EVENT_TYPE = 4;
/** IncrementalSnapshot `data.source` for a DOM mutation (rrweb `IncrementalSource.Mutation`). */
const RRWEB_MUTATION_SOURCE = 0;

/**
 * DOM attributes whose value is a URL that can carry an OAuth callback. rrweb's
 * FullSnapshot serialises them verbatim, so a link/anchor/iframe/form on the
 * callback page would reintroduce the `code`/`state` that the Meta scrub just
 * removed. `xlink:href` covers inline SVG `<use>`/`<image>`.
 */
const RYN_REPLAY_URL_ATTRIBUTES: readonly string[] = [
  'href',
  'src',
  'action',
  'formaction',
  'poster',
  'cite',
  'background',
  'xlink:href',
];

/**
 * Only rewrite an attribute that actually carries a secret shape: a query
 * string, a fragment holding a `key=value` (implicit-flow tokens), or a PATH
 * that a known credential-route marker would redact. This spares ordinary
 * in-page anchors (`#section`) and plain route and
 * asset URLs so the recording still plays back, while catching every OAuth
 * callback (`?code=…&state=…` or `#access_token=…`) AND the path-borne
 * reset/activation token that has no query or fragment at all (bug #33910: a
 * DOM anchor to `/public/password-reset/<token>` otherwise slipped the gate).
 */
const hasSensitiveUrlShape = (value: string): boolean => {
  if (value.includes('?') || /#.*=/.test(value)) {
    return true;
  }
  const pathname = parseUrlPathname(value);
  return (
    pathname !== undefined && scrubRynReplayPathname(pathname) !== pathname
  );
};

/**
 * Scrub the URL-bearing keys of a serialized attribute map (rrweb stores DOM
 * attributes as a plain `{name: value}` object, both in a FullSnapshot node and
 * in an IncrementalSnapshot attribute-mutation record). Returned by reference
 * when nothing changed, so an untouched map is never cloned.
 */
const scrubRynReplayAttributeMap = (
  attributes: Record<string, unknown>
): Record<string, unknown> => {
  let changed = false;
  const next: Record<string, unknown> = { ...attributes };
  for (const attr of RYN_REPLAY_URL_ATTRIBUTES) {
    const value = next[attr];
    if (typeof value === 'string' && hasSensitiveUrlShape(value)) {
      next[attr] = scrubRynReplayUrl(value);
      changed = true;
    }
  }
  return changed ? next : attributes;
};

/**
 * Return a copy of an rrweb serialized-node tree with sensitive URLs scrubbed
 * out of its attributes. Nodes with nothing to change are returned by reference,
 * so an untouched subtree is never cloned.
 */
const scrubRynReplayNodeUrls = (node: unknown): unknown => {
  if (!node || typeof node !== 'object') {
    return node;
  }
  const n = node as {
    attributes?: Record<string, unknown>;
    childNodes?: unknown[];
  };
  let changed = false;

  let attributes = n.attributes;
  if (attributes && typeof attributes === 'object') {
    const scrubbedAttrs = scrubRynReplayAttributeMap(attributes);
    if (scrubbedAttrs !== attributes) {
      attributes = scrubbedAttrs;
      changed = true;
    }
  }

  let childNodes = n.childNodes;
  if (Array.isArray(childNodes)) {
    const scrubbedKids = childNodes.map(scrubRynReplayNodeUrls);
    if (scrubbedKids.some((kid, i) => kid !== (childNodes as unknown[])[i])) {
      childNodes = scrubbedKids;
      changed = true;
    }
  }

  return changed ? { ...n, attributes, childNodes } : node;
};

/**
 * Scrub the mutation payload of an IncrementalSnapshot (type 3, source 0):
 * `adds[]` newly-serialized node subtrees and `attributes[]` change records
 * (`{id, attributes: {href, ...}}`). A credential link Angular renders or
 * updates after first paint lands here, not in the FullSnapshot, so both need
 * the same scrub (bug #33910, found on PR 3642 cross-review). Reference-
 * preserving, so a URL-free mutation is never cloned and the hot path stays cheap.
 */
const scrubRynReplayMutationData = (data: unknown): unknown => {
  if (!data || typeof data !== 'object') {
    return data;
  }
  const d = data as { adds?: unknown[]; attributes?: unknown[] };
  let changed = false;

  let adds = d.adds;
  if (Array.isArray(adds)) {
    const nextAdds = adds.map((add) => {
      if (!add || typeof add !== 'object') {
        return add;
      }
      const a = add as { node?: unknown };
      if (!a.node || typeof a.node !== 'object') {
        return add;
      }
      const scrubbedNode = scrubRynReplayNodeUrls(a.node);
      return scrubbedNode === a.node ? add : { ...a, node: scrubbedNode };
    });
    if (nextAdds.some((a, i) => a !== (adds as unknown[])[i])) {
      adds = nextAdds;
      changed = true;
    }
  }

  let attributes = d.attributes;
  if (Array.isArray(attributes)) {
    const nextAttrs = attributes.map((rec) => {
      if (!rec || typeof rec !== 'object') {
        return rec;
      }
      const r = rec as { attributes?: unknown };
      if (!r.attributes || typeof r.attributes !== 'object') {
        return rec;
      }
      const scrubbed = scrubRynReplayAttributeMap(
        r.attributes as Record<string, unknown>
      );
      return scrubbed === r.attributes ? rec : { ...r, attributes: scrubbed };
    });
    if (nextAttrs.some((a, i) => a !== (attributes as unknown[])[i])) {
      attributes = nextAttrs;
      changed = true;
    }
  }

  return changed ? { ...d, adds, attributes } : data;
};

/**
 * Scrub the raw URLs rrweb stores INSIDE an event payload, before the recorder
 * buffers it. Bugs #31380 and #31385.
 *
 * `scrubRynReplayUrl` (ryn-replay-url-scrub.ts) guards the URL the recorder stamps on the ENVELOPE.
 * It does not touch what rrweb writes into the payload, and rrweb writes the raw
 * page URL in two events:
 *
 *   - Meta (type 4): `data.href` is the raw `location.href`. On an OAuth
 *     callback that is the full `?code=…&state=…`. (#31380, confirmed.)
 *   - FullSnapshot (type 2): the serialised DOM keeps every anchor/link/iframe
 *     URL verbatim, so a node on the callback page re-carries the same
 *     credential. (#31385.)
 *
 *   - IncrementalSnapshot (type 3, source 0): a post-first-paint DOM mutation
 *     whose `adds[]`/`attributes[]` carry an Angular-rendered credential link
 *     that never appears in the FullSnapshot. (#33910, PR 3642 cross-review.)
 *
 * Every other event type (and every non-mutation IncrementalSnapshot) is
 * returned by reference untouched, keeping the incremental hot path cheap.
 */
const scrubRynReplayEventUrls = (event: unknown): unknown => {
  if (!event || typeof event !== 'object') {
    return event;
  }
  const typed = event as {
    type?: number;
    data?: { href?: unknown; node?: unknown };
  };

  if (typed.type === RRWEB_META_EVENT_TYPE) {
    const data = typed.data;
    if (data && typeof data.href === 'string') {
      return {
        ...(event as object),
        data: { ...data, href: scrubRynReplayUrl(data.href) },
      };
    }
    return event;
  }

  if (typed.type === RRWEB_FULL_SNAPSHOT_EVENT_TYPE) {
    const data = typed.data;
    if (data && data.node && typeof data.node === 'object') {
      const scrubbedNode = scrubRynReplayNodeUrls(data.node);
      if (scrubbedNode !== data.node) {
        return {
          ...(event as object),
          data: { ...data, node: scrubbedNode },
        };
      }
    }
    return event;
  }

  if (typed.type === RRWEB_INCREMENTAL_SNAPSHOT_EVENT_TYPE) {
    const data = typed.data as { source?: number } | undefined;
    if (data && data.source === RRWEB_MUTATION_SOURCE) {
      const scrubbed = scrubRynReplayMutationData(data);
      if (scrubbed !== data) {
        return { ...(event as object), data: scrubbed };
      }
    }
    return event;
  }

  return event;
};

/**
 * The single seam every rrweb event passes through before the recorder buffers it: the URL scrub above, then the
 * SSN mask (bug 35675, ryn-replay-rrweb-ssn-scrub.ts), which runs whatever the app's masking policy says.
 */
export const sanitizeRynReplayEvent = (event: unknown): unknown =>
  maskRynReplayEventSsn(scrubRynReplayEventUrls(event));
